<template>
  <div>
    <b-overlay :show="isSending" no-wrap>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="pen" font-scale="3" animation="cylon"></b-icon>
        </div>
      </template>
    </b-overlay>
    <div class="title mb-3">MOSTRA RISULTATI</div>
    <b-container class="mt-4"
                 v-show="availablePhases.length > 1 || availableSessions.length > 1 || availableRanksets.length > 1">
      <b-row class="header text-center">
        <b-col><!--***-->Fase</b-col>
        <b-col><!--***-->Sessione</b-col>
        <b-col><!--***-->Girone</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col>
          <combo-or-label v-model="selectedPhase" :options="availablePhases"></combo-or-label>
        </b-col>
        <b-col>
          <combo-or-label v-model="selectedSession" :options="availableSessions"></combo-or-label>
        </b-col>
        <b-col>
          <combo-or-label v-model="selectedRankset" :options="availableRanksets"></combo-or-label>
        </b-col>
      </b-row>
    </b-container>
    <div>
      <select-with-buttons :options="availableBoards" @click="changeBoard"
                           :selected="currentBoard != null ? currentBoard.Number : 0"></select-with-buttons>
    </div>
    <template v-if="currentBoard != null">
      <deal v-if="currentDeal.Hand != null" :deal="currentDeal" :overlay="currentDetailsOverlay"
            :dealNumber="selectedBoard"
            class="mt-5" ref="deal"></deal>
        <div v-for="alter in availableAlternatives" :key="alter" class="mt-5">
          <div v-if="availableAlternatives.length > 1">Variante {{ alter }}</div>
          <b-table :fields="fieldsToBeShown" :items="scoreToBeShown(alter)" primary-key="key" tbody-class="resultRow">
            <template #cell(points)="row">
              <template v-if="row.item.Alter === -1">
                <b-spinner small variant="success"></b-spinner>
              </template>
              <template v-else>
                <span v-if="row.item.PercNs != null">
                  <span v-text="bankersRound(row.item.PercNs)"
                        :class="row.item.NumNs === pairId ? 'font-weight-bold' : null"></span>/<span
                    v-text="bankersRound(row.item.PercEw)"
                    :class="row.item.NumEw === pairId ? 'font-weight-bold' : null"></span>
                </span>
                <span v-else :class="computeScoreColor(row.item.PuntiNs)">{{ row.item.PuntiNs }}</span>
              </template>
            </template>
            <template #cell(TextualContract)="row">
              <div v-html="row.value"></div>
            </template>
            <template #cell(TextualLead)="row">
              <div v-html="row.value"></div>
            </template>

            <template #cell(show_details)="row">
              <b-icon-search v-if="!row.detailsShowing" @click="openDetails(row.item.key)"></b-icon-search>
            </template>

            <template #row-details="row">
              <div class="position-relative">
              <result-details :details="currentRowDetails(row.item)" @askedOverlay="overlayDetails"
                              :askForOverlay="currentDeal.Hand != null && currentDeal.Hand.N != null"></result-details>
                <div style="font-size: 1.5rem;">

              <b-icon-trash v-if="isTd && canDeleteScore(row.item)"
                            @click="deleteScore(row.item)"
                            variant="danger" style="position: absolute; right: 2%; top: 0%;"
              ></b-icon-trash>
              </div>
              </div>
            </template>
          </b-table>
        </div>
    </template>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import SelectWithButtons from '@/components/lib/SelectWithButtons';
import Deal from '@/components/display/Deal';
import ComboOrLabel from "@/components/lib/ComboOrLabel";
import ResultDetails from "../components/display/ResultDetails";
import {addColorToSuite as _addColorToSuite} from "../lib/cardSuits";
import {fetchCustomMethod} from "@/lib/fetcher";

export default {
  name: 'ScoreViewer',
  components: {ResultDetails, ComboOrLabel, Deal, SelectWithButtons},
  data() {
    return {
      selectedBoard: 0,
      currentDetailsId: "",
      currentDetailsOverlay: null,
      selectedPhase: 1,
      selectedSession: 1,
      selectedRankset: 1,
      isSending: false
    };
  },
  computed: {
    ...mapGetters({
      playerCurrentSessionMap: 'players/playerCurrentSessionMap',
      pairId: 'tournament/pairId',
      sectors: 'tournament/sector',
      globalDeals: 'deals/played',
      pairs: 'players/pairsList',
      players: 'players/playersList',
      isTd: 'webApp/isTd',
    }),
    availablePhases() {
      return Object.keys(this.globalDeals.Phases).sort((a, b) => a - b);
    },
    availableSessions() {
      return Object.keys(
          this.globalDeals.Phases[this.selectedPhase]?.Sessions ?? {}
      ).sort((a, b) => a - b);
    },
    availableRanksets() {
      return Object.keys(
          this.globalDeals
              .Phases[this.selectedPhase]
              ?.Sessions[this.selectedSession]
              ?.Ranksets ?? {}
      ).map(rkset => {
        return {
          value: rkset,
          text: this.globalDeals
              .Phases[this.selectedPhase]
              .Sessions[this.selectedSession]
              .Ranksets[rkset]
              .GroupedSectors.join('-')
        }
      }).sort((a, b) => a.value - b.value);
    },
    availableBoards() {
      return Object.keys(
          this.globalDeals
              .Phases[this.selectedPhase]
              ?.Sessions[this.selectedSession]
              ?.Ranksets[this.selectedRankset]
              ?.Boards ?? {}
      )
          .sort((a, b) => a - b)
          .map(key => {
            return {value: key, text: key, class: ['scoreButton']};
          });
    },
    availableAlternatives() {
      let temp = this.currentBoard.Scores?.filter(res => res.Score !== '').map(res => Math.max(0, res.Alter));
      return [...new Set(temp)].sort((a, b) => a - b);
    },
    currentBoard() {
      return this.globalDeals
          .Phases[this.selectedPhase]
          ?.Sessions[this.selectedSession]
          ?.Ranksets[this.selectedRankset]
          ?.Boards[this.selectedBoard] ?? null
    },
    currentDeal() {
      return this.globalDeals
          .Phases[this.selectedPhase]
          ?.Sessions[this.selectedSession]
          ?.Ranksets[this.selectedRankset]
          ?.Deals[this.selectedBoard] ?? {}
    },
    fieldsToBeShown() {
      return [
        {
          key: 'TextualContract',
          label: /*'****/'Contr.',
          formatter: this.addColorToSuite
        },
        {
          key: 'TextualLead',
          label: '',
          formatter: this.addColorToSuite
        },
        {
          key: 'TextualScore',
          label: ''
        },
        {
          key: 'points',
          label: /*'****/'Pt.'
        },
        {
          key: 'show_details',
          label: ''
        }
      ];
    }
  },
  methods: {
    overlayDetails(details) {
      this.currentDetailsOverlay = details;

      var element = this.$refs["deal"].$el;
      this.$scrollTo(element, {offset: -30});
    },
    computeScoreColor(points) {
      const intPoints = parseInt(points);
      return intPoints > 0 ? "blueResult" : intPoints < 0 ? "redResult" : "yellowResult";
    },
    changeBoard(number) {
      this.selectedBoard = number;

      this.currentDetailsOverlay = null;

      const that = this;
      that.$nextTick(() => {
            const ourResult = Object.values(this.currentBoard.Scores)
                .filter(res => res.Score !== '')
                .find(row => row.NSex === that.pairId || row.EWex === that.pairId);

            if (ourResult != null && this.currentBoard.Hand != null && this.currentBoard.Hand.N != null) {
              that.overlayDetails(that.currentRowDetails(ourResult));
            }
          }
      );
    },
    bankersRound(number) {
      var r = Math.round(number);
      return Math.abs(number) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r;
    },
    openDetails(number) {
      this.currentDetailsId = number;
    },
    closeDetails() {
      this.currentDetailsId = "";
    },
    scoreToBeShown(alter) {
      const that = this;
      return this.currentBoard.Scores
          .filter(res => res.Score !== '' && (res.Alter === alter || (alter === 0 && res.Alter === -1)))
          .map(row => {
            const ret = {...row};
            ret._rowVariant = row.NSex === that.pairId || row.EWex === that.pairId ? "warning" : "";
            ret.key = ret.BoardNumber + "_" + ret.Round + "_" + ret.Series + "_" + ret.Group + "_" + ret.Table + "_" + ret.Alter;
            ret._showDetails = ret.key === that.currentDetailsId;
            return ret;
          }).sort((a, b) => {
            return parseInt(b.PuntiNs) - parseInt(a.PuntiNs)
          });
    },
    deleteScore(row) {

      const toBeSent = {
        board: this.selectedBoard,
        table: row.Table,
        round: row.Round,
        serie: row.Series,
        room: 0,
        group: row.Group,
        session: row.Session,
        declarer: 0,
        lead: row.Lead,
        contract: ""
      };
      this.$nextTick(() => {
        this.isSending = true;
        fetchCustomMethod('/api/score', "DELETE", toBeSent)
            .then(resp => {
              if (resp.success) {
                setTimeout(() => this.isSending = false, 5000);
              } else {
                alert("Errore nella cancellazione");
                this.isSending = false;
              }
            });
      });
    },
    currentRowDetails(row) {
      const round = row.Round;
      const groupId = row.Group;
      const phase = row.Phase;
      const series = row.Series;
      const session = row.Session;
      const table = row.Table;

      const group = this.sectors.Phases[phase].Series[series].Sessions[session].Groups[groupId];

      const currentTableStatus = group.Rounds[round].Tables[table]

      const nsPair = this.pairs[currentTableStatus.NsEventId];
      const ewPair = this.pairs[currentTableStatus.EwEventId];


      return {
        N: this.players[nsPair.IdGiocatore1].Fullname,
        S: this.players[nsPair.IdGiocatore2].Fullname,
        E: this.players[ewPair.IdGiocatore1].Fullname,
        W: this.players[ewPair.IdGiocatore2].Fullname,
        ...row
      };
    },
    canDeleteScore(row) {
      const phase = row.Phase;
      const series = row.Series;
      const session = row.Session;

      const serieCurrentSession = this.sectors.Phases[phase].Series[series].CurrentSession;

      return session === serieCurrentSession;
    },
    addColorToSuite(contract) {
      return _addColorToSuite(contract);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.selectedPhase = Math.max(...this.availablePhases);
      this.$nextTick(() => {
        this.selectedSession = Math.max(...this.availableSessions);
      });
    });
  }

}

</script>

<style scoped>

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  color: #3396B8;
}

.blueResult {
  color: #2661CE;
}

.redResult {
  color: red;
}

.yellowResult {
  color: darkviolet;
}

>>> .resultRow {
  line-height: 20px;
}

>>> .scoreButton {
  background: white;
  border: 1px solid #3396B8;
}
</style>
